import { inject, Injectable, PLATFORM_ID } from '@angular/core';

import { EnvironmentService } from 'src/services/environment/environment.service';
import { LocaleService } from 'src/services/locale/locale.service';
import type { IHeaderSubmenu, IHeaderSubmenuSection } from 'src/shared/types/header';
import type { ILocalizableLink } from 'src/shared/types/localizable-link';

@Injectable({ providedIn: 'root' })
export class LayoutLocalizationService {
    private platformId = inject(PLATFORM_ID);
    private readonly localeService = inject(LocaleService);
    private readonly environmentService = inject(EnvironmentService);

    public getLocalizableUrl(link: ILocalizableLink): string | null {
        if (this.checkIsItemHiddenForCurrentLocale(link)) {
            return null;
        }

        if (link.localeLinks) {
            return this.getLocalizedPathFromLocaleLinks(link.url, link.localeLinks, link.isAbsoluteLink);
        }

        if (link.locales) {
            return this.getLocalizedPath(link.url, link.locales);
        }

        return link.url;
    }

    public checkIsItemHiddenForCurrentLocale(item: ILocalizableLink | IHeaderSubmenuSection | IHeaderSubmenu): boolean {
        return !!item.displayOnly && item.displayOnly.indexOf(this.localeService.getCurrentLocaleSlug()) === -1;
    }

    private getLocalizedPath(path: string, localeSlugs: string[]): string {
        const currentLocaleSlug = this.localeService.getCurrentLocaleSlug();

        if (localeSlugs.includes(currentLocaleSlug)) {
            const localePath = this.localeService.getPathToLocaleSlug(currentLocaleSlug);

            return this.environmentService.environment.origin + localePath + path;
        } else {
            return this.environmentService.environment.origin + path;
        }
    }

    private getLocalizedPathFromLocaleLinks(
        defaultPath: string,
        localeLinks: {
            [locale: string]: string;
        },
        isAbsoluteLink?: boolean
    ): string {
        const currentLocaleSlug = this.localeService.getCurrentLocaleSlug();

        if (Object.keys(localeLinks).includes(currentLocaleSlug)) {
            const localePath = this.localeService.getPathToLocaleSlug(currentLocaleSlug);

            if (isAbsoluteLink) {
                return localeLinks[currentLocaleSlug];
            } else {
                return this.environmentService.environment.origin + localePath + localeLinks[currentLocaleSlug];
            }
        } else {
            return isAbsoluteLink ? defaultPath : this.environmentService.environment.origin + defaultPath;
        }
    }
}
